<div class="max-w-7xl mx-auto py-8 px-4">
  <h2 class="text-3xl font-bold text-green-800 mb-8 text-center">Checkout</h2>

  @if (cart$ | async; as cart) {
    <ng-container [ngTemplateOutlet]="cart.items.length > 0 ? cartItems : emptyCart"
      [ngTemplateOutletContext]="{$implicit: cart}">
    </ng-container>
  }

  <ng-template #cartItems let-cart>
    <div class="flex flex-col lg:flex-row gap-8">
      <!-- Cart Section -->
      <div class="w-full lg:w-1/2">
        <div class="md:sticky md:top-4">
          <!-- Mobile Cart Summary -->
          <div class="lg:hidden bg-white rounded-lg shadow-sm p-4 mb-4">
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold">Cart Total: {{total$ | async |currency:'AUD'}}</h3>
              <button (click)="isCartExpanded = !isCartExpanded"
                class="text-green-800 hover:text-green-700">
                {{isCartExpanded ? 'Hide' : 'Show'}} Cart Details
              </button>
            </div>
          </div>

          <!-- Cart Content (Collapsible on Mobile) -->
          <div [@expandCollapse]="getAnimationState()"
            class="bg-white rounded-lg shadow-sm"
            [class.lg:block]="!isCartExpanded">
            <div class="flex flex-col gap-6">
              @for (item of cart.items; track item) {
                <div class="flex flex-row gap-4 p-4 bg-white rounded-lg shadow-sm">
                  <a [routerLink]="['/tea-item', item.id]" class="flex-shrink-0">
                    <img [ngSrc]="(item.imageUrl | imageModifier:{width:256} | async) || ''" [alt]="item.name"
                      class="w-24 h-24 md:w-32 md:h-32 object-cover rounded-md dropshadow"
                      width="128"
                      height="128">
                  </a>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold"><a [routerLink]="['/tea-item', item.id]">{{item.name}}</a></h3>
                    <p class="text-xl font-bold text-green-800">{{item.price * item.quantity | currency:'AUD'}}
                      <span class="block font-normal text-xs text-gray-400">{{item.price | currency:'AUD'}} each</span>
                    </p>
                    <div class="flex items-center gap-4 my-4">
                      <button (click)="updateQuantity(item, -1)"
                      class="px-4 py-2 border border-gray-200 rounded-md hover:bg-gray-50">-</button>
                      <span class="font-medium inline-block w-8 text-center">{{item.quantity}}</span>
                      <button (click)="updateQuantity(item, 1)"
                      class="px-4 py-2 border border-gray-200 rounded-md hover:bg-gray-50">+</button>
                    </div>
                    <button (click)="removeItem(item)"
                    class="text-red-600 hover:text-red-700">Remove</button>
                    @if (cartValidation?.items?.[item.id]?.length) {
                      <div
                        class="mt-2 text-red-600 text-sm">
                        @for (validationMsg of cartValidation?.items?.[item.id]; track validationMsg) {
                          <div>
                            {{ validationMsg.message }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Payment and Address Section -->
      <div class="w-full lg:w-1/2">
        <div class="bg-white rounded-lg shadow-sm -p-6">
          <h3 class="text-xl font-bold mb-6 text-green-800">Shipping Options</h3>
          <!-- Shipping Address Validation -->
          <div class="space-y-6" [class.hidden]="getShippingAddress()">
            <div class="border border-gray-200 rounded-lg p-4">
              <!-- <h4 class="font-semibold mb-2">Shipping Address</h4> -->
              <p class="text-gray-500 pb-2">
                Please enter your shipping address to find available shipping options.
              </p>
              <div #shippingAddress></div>
              <button (click)="revalidateShippingDetails()"
                [disabled]="isValidatingAddress"
                class="mt-4 w-full py-2 bg-green-800 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                @if (!isValidatingAddress) {
                  <span>Check Shipping Options</span>
                }
                @if (isValidatingAddress) {
                  <span class="flex items-center justify-center gap-2">
                    <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking...
                  </span>
                }
              </button>
            </div>
            <!-- <div class="border border-gray-200 rounded-lg p-4">
            <h4 class="font-semibold mb-2">Payment Method</h4>
            <p class="text-gray-500">Payment form coming soon...</p>
          </div> -->
        </div>
        <!-- Display Current Shipping Address -->
        @if (getShippingAddress(); as shippingAddress) {
          <div class="border border-gray-200 rounded-lg p-4 mb-4">
            <div class="flex justify-between items-center mb-2">
              <h4 class="font-semibold text-green-800">Shipping Address</h4>
              <button (click)="clearShippingAddress()"
                class="text-green-800 text-sm hover:text-green-600 transition-colors border border-gray-200 rounded px-2 py-0.5 hover:border-green-600"
                >
                change
              </button>
            </div>
            <div class="text-gray-700 space-y-1">
              <p class="font-semibold">{{shippingAddress.name}}</p>
              <p>{{shippingAddress.address.line1}}</p>
              @if (shippingAddress.address.line2) {
                <p>{{shippingAddress.address.line2}}</p>
              }
              <p>{{shippingAddress.address.city}}, {{shippingAddress.address.state}} {{shippingAddress.address.postal_code}}</p>
              <p>{{shippingAddress.address.country}}</p>
            </div>
          </div>
        }

        <!-- Display the list of available shipping options -->
        @if (availableShippingServices.length) {
          <div class="space-y-4">
            <h4 class="font-semibold text-green-800">Available Shipping Services</h4>
            <div class="space-y-2">
              @for (shippingService of availableShippingServices; track shippingService) {
                <div
                  (click)="selectShippingService(shippingService)"
                  [class.border-green-600]="getSelectedShippingService()?.serviceCode === shippingService.serviceCode"
                  class="border rounded-lg p-4 cursor-pointer hover:border-green-600 transition-colors">
                  <div class="flex justify-between items-center">
                    <div>
                      <p class="font-semibold">{{shippingService.serviceName}}</p>
                      <!-- <p class="text-sm text-gray-600">{{shippingService.description}}</p> -->
                      <p class="text-sm text-gray-600">{{shippingService.deliveryTime}}</p>
                    </div>
                    <div class="text-right">
                      <p class="font-bold text-green-800">{{shippingService.price | currency:'AUD'}}</p>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Loading state for shipping options -->
        @if (isLoadingShippingServices) {
          <div class="py-8 flex justify-center">
            <div class="flex items-center gap-2 text-green-800">
              <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading shipping options...
            </div>
          </div>
        }

      </div>
    </div>
  </div>

  <!-- Validation and Checkout Section -->
  <div class="mt-8 p-4 bg-white rounded-lg shadow-sm">
    <h3 class="text-xl font-bold">Total: {{total$ | async |currency:'AUD'}}</h3>
    @if (cartValidation?.totalAmount?.status !== 'success') {
      <div
        class="mt-2 text-red-600 text-sm">
        {{ cartValidation?.totalAmount?.message }}
      </div>
    }
    <button (click)="validateCart()"
      [disabled]="isValidating"
      class="block w-full mt-4 py-4 bg-green-800 text-white rounded-md hover:bg-green-700 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed">
      @if (!isValidating) {
        <span>Proceed to Checkout</span>
      }
      @if (isValidating) {
        <span class="flex items-center justify-center gap-2">
          <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Validating...
        </span>
      }
    </button>
  </div>
</ng-template>

<ng-template #emptyCart>
  <div class="pb-4 bg-white rounded-lg shadow-sm flex flex-col md:flex-row md:h-96">
    <div class="flex items-center justify-center md:w-1/2">
      <div class="flex flex-col items-center space-y-4">
        <p class="text-gray-500 text-2xl text-center">Your cart is empty.</p>
        <p class="text-gray-400 text-lg text-center">Browse our collection to find your perfect tea!</p>
        <p class="text-gray-400 text-lg text-center">Would you like some assistance?</p>
        <div class="w-full flex flex-col md:flex-row gap-4">
          <button routerLink="/home"
            class="w-full md:w-40 px-6 py-2 bg-green-800 text-white rounded-md hover:bg-green-700 transition-colors">
            Shop Now
          </button>
          <button routerLink="/contact"
            class="w-full md:w-40 px-6 py-2 bg-green-800 text-white rounded-md hover:bg-green-700 transition-colors">
            Contact Us
          </button>
          <!-- <button routerLink="/home"
          class="w-full md:w-40 px-6 py-2 bg-green-800 text-white rounded-md hover:bg-green-700 transition-colors">
          Shopping Assistant
        </button> -->
      </div>
    </div>
  </div>
  <div class="flex items-center justify-center md:w-1/2">
    <img [ngSrc]="('/assets/tea-type-lady/white-tea-lady.png'| imageModifier:{width:450} | async) || ''"
      alt="Empty Cart"
      class="w-1/2 md:w-2/3 pt-4"
      width="450"
      height="450">
  </div>
</div>
</ng-template>

<tea-shop-fe-home-carousel></tea-shop-fe-home-carousel>
</div>
